import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { Input } from '../../ui/Input'
import { Select } from '../../ui/Select'
import { Button } from '../../ui/Button'
import vehicleCaptureService from '../../../services/vehicleCaptureService'

const tipoOptions = [
  { value: 'Nuevo', label: 'Nuevo' },
  { value: 'Usado', label: 'Usado' },
]

const tipoActivoOptions = [
  { value: '1', label: 'Auto' },
  { value: '2', label: 'Bicicleta' },
  { value: '3', label: 'Tracto/maquina' },
]

// Años válidos para "Modelo": el actual, uno antes y uno después.
const currentYear = new Date().getFullYear()
const modeloOptions = [currentYear - 1, currentYear, currentYear + 1].map(
  year => ({ value: String(year), label: String(year) })
)

interface NewLeaseRequestFormValues {
  tipo: string
  tipoActivo: string
  modelo: string
  marca: string
  submarca: string
  version: string
}

const initialValues: NewLeaseRequestFormValues = {
  tipo: '',
  tipoActivo: '',
  modelo: '',
  marca: '',
  submarca: '',
  version: '',
}

const validationSchema = Yup.object({
  tipo: Yup.string()
    .oneOf(tipoOptions.map(option => option.value))
    .required('El tipo es obligatorio'),
  tipoActivo: Yup.string()
    .oneOf(tipoActivoOptions.map(option => option.value))
    .required('El tipo de activo es obligatorio'),
  modelo: Yup.string()
    .oneOf(modeloOptions.map(option => option.value), 'Selecciona un año válido')
    .required('El modelo es obligatorio'),
  marca: Yup.string().trim().required('La marca es obligatoria'),
  submarca: Yup.string().trim().required('La submarca es obligatoria'),
  version: Yup.string().trim().required('La versión es obligatoria'),
})

export const NewLeaseRequestContent: FC = () => {
  const navigate = useNavigate()

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/mis-arrendamientos')}
        className="flex items-center gap-1.5 border border-gray-300 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-olr-grey-1 transition-colors mb-4"
      >
        <ChevronLeft size={16} />
        Regresar
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Solicitar nuevo arrendamiento
      </h2>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const response = await vehicleCaptureService.notifySellerVehicleCapture({
              tipo: values.tipo,
              vehicleType:
                tipoActivoOptions.find(option => option.value === values.tipoActivo)
                  ?.label ?? '',
              year: values.modelo,
              brand: values.marca,
              subBrand: values.submarca,
              version: values.version,
            })

            if (!response.sellerFound) {
              toast.error(
                'No encontramos un vendedor asignado a tu cuenta. Contacta a soporte.'
              )
              return
            }

            if (!response.sent) {
              toast.error(
                'Tu vendedor no tiene notificaciones activas en este momento. Te recomendamos contactarlo directamente.'
              )
            } else {
              toast.success('Tu solicitud fue enviada a tu vendedor.')
            }

            navigate('/mis-arrendamientos')
          } finally {
            // Los errores de red/servidor ya muestran su propio toast vía
            // el interceptor global de api.ts — aquí solo se libera el
            // estado de envío del formulario.
            setSubmitting(false)
          }
        }}
      >
        {({ values, setFieldValue, errors, touched, handleBlur, isSubmitting }) => (
          <Form className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="Tipo*"
                value={values.tipo}
                onValueChange={value => setFieldValue('tipo', value)}
                options={tipoOptions}
                placeholder="Selecciona"
                onBlur={handleBlur}
                error={touched.tipo && errors.tipo ? errors.tipo : undefined}
              />
              <Select
                label="Tipo de activo*"
                value={values.tipoActivo}
                onValueChange={value => setFieldValue('tipoActivo', value)}
                options={tipoActivoOptions}
                placeholder="Selecciona"
                onBlur={handleBlur}
                error={
                  touched.tipoActivo && errors.tipoActivo
                    ? errors.tipoActivo
                    : undefined
                }
              />
              <Select
                label="Modelo*"
                value={values.modelo}
                onValueChange={value => setFieldValue('modelo', value)}
                options={modeloOptions}
                placeholder="Selecciona"
                onBlur={handleBlur}
                error={
                  touched.modelo && errors.modelo ? errors.modelo : undefined
                }
              />
              <Input
                label="Marca*"
                name="marca"
                placeholder="Ingresa la marca"
                value={values.marca}
                onChange={e => setFieldValue('marca', e.target.value)}
                onBlur={handleBlur}
                error={
                  touched.marca && errors.marca ? errors.marca : undefined
                }
              />
              <Input
                label="Submarca*"
                name="submarca"
                placeholder="Ingresa la submarca"
                value={values.submarca}
                onChange={e => setFieldValue('submarca', e.target.value)}
                onBlur={handleBlur}
                error={
                  touched.submarca && errors.submarca
                    ? errors.submarca
                    : undefined
                }
              />
              <Input
                label="Versión*"
                name="version"
                placeholder="Ingresa la versión"
                value={values.version}
                onChange={e => setFieldValue('version', e.target.value)}
                onBlur={handleBlur}
                error={
                  touched.version && errors.version
                    ? errors.version
                    : undefined
                }
              />
            </div>

            {/* Campos obligatorios */}
            <div className="pt-4">
              <p className="text-sm text-gray-600">*Campos obligatorios</p>
            </div>

            <div className="flex justify-start">
              <Button
                type="submit"
                isLoading={isSubmitting}
                className="px-12 py-3 min-w-[230px]"
              >
                Cotizar
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default NewLeaseRequestContent
